import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { VariantProps, cva } from "class-variance-authority";
import {
  PanelRight,
  X,
  Settings,
  Info,
  Edit3,
  Palette,
  Database,
  Users,
  Shield,
} from "lucide-react";

import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/InputDefault";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/user-feedback/tooltip";

const RIGHT_SIDEBAR_WIDTH = "28rem";
const RIGHT_SIDEBAR_WIDTH_MOBILE = "30rem";
const RIGHT_SIDEBAR_KEYBOARD_SHORTCUT = "r";

// Context types for different use cases
export type RightSidebarContext =
  | "element-settings"
  | "element-info"
  | "element-edit"
  | "theme-settings"
  | "data-settings"
  | "user-settings"
  | "security-settings"
  | "custom";

export interface RightSidebarConfig {
  context: RightSidebarContext;
  title: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
  content: React.ReactNode;
  actions?: React.ReactNode;
  onClose?: () => void;
  onSave?: () => void;
  onReset?: () => void;
  isDirty?: boolean;
  showSaveButton?: boolean;
  showResetButton?: boolean;
  showCloseButton?: boolean;
}

type RightSidebarContextType = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  config: RightSidebarConfig | null;
  setConfig: (config: RightSidebarConfig | null) => void;
  isMobile: boolean;
  openRightSidebar: (config: RightSidebarConfig) => void;
  closeRightSidebar: () => void;
};

const RightSidebarContext = React.createContext<RightSidebarContextType | null>(
  null
);

export function useRightSidebar() {
  const { t } = usePlatformTranslation();
  const context = React.useContext(RightSidebarContext);
  if (!context) {
    throw new Error(
      "useRightSidebar must be used within a RightSidebarProvider."
    );
  }
  return context;
}

const RightSidebarProvider = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, style, children, ...props }, ref) => {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = React.useState(false);
  const [config, setConfig] = React.useState<RightSidebarConfig | null>(null);

  const openRightSidebar = React.useCallback(
    (newConfig: RightSidebarConfig) => {
      setConfig(newConfig);
      setIsOpen(true);
    },
    []
  );

  const closeRightSidebar = React.useCallback(() => {
    setIsOpen(false);
    // Small delay to allow for smooth animations
    setTimeout(() => {
      setConfig(null);
    }, 200);
  }, []);

  // Keyboard shortcut to close the right sidebar
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        event.preventDefault();
        closeRightSidebar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeRightSidebar]);

  const contextValue = React.useMemo<RightSidebarContextType>(
    () => ({
      isOpen,
      setIsOpen,
      config,
      setConfig,
      isMobile,
      openRightSidebar,
      closeRightSidebar,
    }),
    [isOpen, config, isMobile, openRightSidebar, closeRightSidebar]
  );

  return (
    <RightSidebarContext.Provider value={contextValue}>
      <TooltipProvider delayDuration={100} skipDelayDuration={0}>
        <div
          style={
            {
              "--right-sidebar-width": RIGHT_SIDEBAR_WIDTH,
              ...style,
            } as React.CSSProperties
          }
          className={className}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      </TooltipProvider>
    </RightSidebarContext.Provider>
  );
});
RightSidebarProvider.displayName = "RightSidebarProvider";

const RightSidebar = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    variant?: "sidebar" | "floating" | "inset";
  }
>(({ variant = "sidebar", className, children, ...props }, ref) => {
  const { isOpen, isMobile, config } = useRightSidebar();

  if (!config) {
    return null;
  }

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={() => {}} {...props}>
        <SheetContent
          data-right-sidebar="sidebar"
          data-mobile="true"
          className="w-[--right-sidebar-width] bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden"
          style={
            {
              "--right-sidebar-width": RIGHT_SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
          side="right"
        >
          <div className="flex h-full w-full flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      ref={ref}
      className={cn(
        "duration-200 fixed inset-y-0 z-50 h-svh transition-[right,width] ease-linear",
        "right-0",
        isOpen
          ? "w-[--right-sidebar-width]"
          : "w-0 opacity-0 pointer-events-none",
        variant === "floating" || variant === "inset"
          ? "p-2"
          : "border-l border-sidebar-border",
        className
      )}
      {...props}
    >
      <div
        data-right-sidebar="sidebar"
        className="flex h-full w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow"
      >
        {children}
      </div>
    </div>
  );
});
RightSidebar.displayName = "RightSidebar";

const RightSidebarTrigger = React.forwardRef<
  React.ElementRef<typeof Button>,
  React.ComponentProps<typeof Button> & {
    config: RightSidebarConfig;
  }
>(({ className, onClick, config, ...props }, ref) => {
  const { openRightSidebar } = useRightSidebar();

  return (
    <Button
      ref={ref}
      data-right-sidebar="trigger"
      variant="ghost"
      size="icon"
      className={cn("h-7 w-7", className)}
      onClick={(event) => {
        onClick?.(event);
        openRightSidebar(config);
      }}
      {...props}
    >
      <PanelRight />
      <span className="sr-only">{t('platform.layout.side-bars.open_right_sidebar')}</span>
    </Button>
  );
});
RightSidebarTrigger.displayName = "RightSidebarTrigger";

const RightSidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    showCloseButton?: boolean;
    onClose?: () => void;
  }
>(({ className, showCloseButton = true, onClose, children, ...props }, ref) => {
  const { closeRightSidebar } = useRightSidebar();

  const handleClose = () => {
    onClose?.();
    closeRightSidebar();
  };

  return (
    <div
      ref={ref}
      data-right-sidebar="header"
      className={cn(
        "flex items-center justify-between p-4 border-b border-sidebar-border",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={handleClose}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">{t('platform.common.close')}</span>
        </Button>
      )}
    </div>
  );
});
RightSidebarHeader.displayName = "RightSidebarHeader";

const RightSidebarContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-right-sidebar="content"
      className={cn("flex-1 min-h-0 overflow-auto p-4", className)}
      {...props}
    />
  );
});
RightSidebarContent.displayName = "RightSidebarContent";

const RightSidebarFooter = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-right-sidebar="footer"
      className={cn(
        "flex flex-col gap-2 p-4 border-t border-sidebar-border bg-sidebar flex-shrink-0",
        className
      )}
      {...props}
    />
  );
});
RightSidebarFooter.displayName = "RightSidebarFooter";

const RightSidebarSeparator = React.forwardRef<
  React.ElementRef<typeof Separator>,
  React.ComponentProps<typeof Separator>
>(({ className, ...props }, ref) => {
  return (
    <Separator
      ref={ref}
      data-right-sidebar="separator"
      className={cn("mx-4 w-auto bg-sidebar-border", className)}
      {...props}
    />
  );
});
RightSidebarSeparator.displayName = "RightSidebarSeparator";

// Context-specific components
const ContextIcon = ({ context }: { context: RightSidebarContext }) => {
  const iconMap = {
    "element-settings": Settings,
    "element-info": Info,
    "element-edit": Edit3,
    "theme-settings": Palette,
    "data-settings": Database,
    "user-settings": Users,
    "security-settings": Shield,
    custom: Settings,
  };

  const IconComponent = iconMap[context] || Settings;
  return <IconComponent className="h-5 w-5" />;
};

const RightSidebarTitle = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    config: RightSidebarConfig;
  }
>(({ className, config, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn("flex items-center gap-3", className)}
      {...props}
    >
      {config.icon ? (
        <config.icon className="h-5 w-5 text-sidebar-foreground" />
      ) : (
        <ContextIcon context={config.context} />
      )}
      <div className="flex flex-col">
        <h2 className="text-lg font-semibold text-sidebar-foreground">
          {config.title}
        </h2>
        {config.description && (
          <p className="text-sm text-sidebar-foreground/70">
            {config.description}
          </p>
        )}
      </div>
    </div>
  );
});
RightSidebarTitle.displayName = "RightSidebarTitle";

const RightSidebarActions = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    config: RightSidebarConfig;
  }
>(({ className, config, ...props }, ref) => {
  const { closeRightSidebar } = useRightSidebar();

  const handleSave = () => {
    config.onSave?.();
    if (!config.isDirty) {
      closeRightSidebar();
    }
  };

  const handleReset = () => {
    config.onReset?.();
  };

  return (
    <div ref={ref} className={cn("flex gap-2", className)} {...props}>
      {config.showResetButton && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleReset}
          disabled={!config.isDirty}
        >
          Reset
        </Button>
      )}
      {config.showSaveButton && (
        <Button size="sm" onClick={handleSave} disabled={!config.isDirty}>
          Save
        </Button>
      )}
      {config.actions}
    </div>
  );
});
RightSidebarActions.displayName = "RightSidebarActions";

// Main RightSidebar component that uses the config
const RightSidebarWithConfig = () => {
  const { config, isOpen } = useRightSidebar();

  if (!config || !isOpen) {
    return null;
  }

  return (
    <RightSidebar>
      <RightSidebarHeader>
        <RightSidebarTitle config={config} />
      </RightSidebarHeader>

      <RightSidebarContent>{config.content}</RightSidebarContent>

      {(config.showSaveButton || config.showResetButton || config.actions) && (
        <RightSidebarFooter>
          <RightSidebarActions config={config} />
        </RightSidebarFooter>
      )}
    </RightSidebar>
  );
};

// Hook for easy usage
export const useRightSidebarConfig = () => {
  const { openRightSidebar, closeRightSidebar } = useRightSidebar();

  return {
    openElementSettings: (
      elementId: string,
      elementData: Record<string, unknown>
    ) => {
      openRightSidebar({
        context: "element-settings",
        title: "Element Settings",
        description: `Configure settings for ${elementId}`,
        content: <div>Settings content for {elementId}</div>,
        showSaveButton: true,
        showResetButton: true,
        isDirty: false,
      });
    },
    openElementInfo: (
      elementId: string,
      elementData: Record<string, unknown>
    ) => {
      openRightSidebar({
        context: "element-info",
        title: "Element Information",
        description: `Details about ${elementId}`,
        content: <div>Info content for {elementId}</div>,
        showCloseButton: true,
      });
    },
    openThemeSettings: () => {
      openRightSidebar({
        context: "theme-settings",
        title: "Theme Settings",
        description: "Customize the appearance",
        content: <div>{t('platform.layout.side-bars.theme_settings_content')}</div>,
        showSaveButton: true,
        showResetButton: true,
        isDirty: false,
      });
    },
    openDataSettings: () => {
      openRightSidebar({
        context: "data-settings",
        title: "Data Settings",
        description: "Configure data preferences",
        content: <div>{t('platform.layout.side-bars.data_settings_content')}</div>,
        showSaveButton: true,
        showResetButton: true,
        isDirty: false,
      });
    },
    openUserSettings: () => {
      openRightSidebar({
        context: "user-settings",
        title: "User Settings",
        description: "Manage your preferences",
        content: <div>{t('platform.layout.side-bars.user_settings_content')}</div>,
        showSaveButton: true,
        showResetButton: true,
        isDirty: false,
      });
    },
    openSecuritySettings: () => {
      openRightSidebar({
        context: "security-settings",
        title: "Security Settings",
        description: "Manage security preferences",
        content: <div>{t('platform.layout.side-bars.security_settings_content')}</div>,
        showSaveButton: true,
        showResetButton: true,
        isDirty: false,
      });
    },
    openCustom: (config: Omit<RightSidebarConfig, "context">) => {
      openRightSidebar({
        ...config,
        context: "custom",
      });
    },
    close: closeRightSidebar,
  };
};

export {
  RightSidebar,
  RightSidebarProvider,
  RightSidebarTrigger,
  RightSidebarHeader,
  RightSidebarContent,
  RightSidebarFooter,
  RightSidebarSeparator,
  RightSidebarTitle,
  RightSidebarActions,
  RightSidebarWithConfig,
};
